import { type I18nFuncs, useI18n } from '@affine/i18n';
import type { ReactElement } from 'react';

import { requestAppResourceJson } from './app-resource-http';
import type { AppResourceKind } from './app-resource-preferences';

export type AppResourceLifecycleOperation =
  | 'move'
  | 'permanent_delete'
  | 'restore'
  | 'trash';

export type AppResourceLifecycleImpactKind =
  | 'linked_resource_requires_same_area'
  | 'provider_actions_disabled'
  | 'public_route_disabled'
  | 'will_be_read_only'
  | 'will_be_removed_from_apps'
  | 'will_be_restored_in_apps';

export interface AppResourceLifecycleImpact {
  accessDocId: string;
  impact: AppResourceLifecycleImpactKind;
  operation: AppResourceLifecycleOperation;
  resourceId: string;
  resourceKind: AppResourceKind;
  title: string;
  workspaceId: string;
}

interface AppResourceLifecycleImpactResponse {
  impacts: AppResourceLifecycleImpact[];
}

export async function fetchDocAppResourceLifecycleImpacts(
  workspaceId: string,
  operation: AppResourceLifecycleOperation,
  docIds: readonly string[],
  targetFolderId?: string | null
): Promise<AppResourceLifecycleImpact[]> {
  const response =
    await requestAppResourceJson<AppResourceLifecycleImpactResponse>(
      appResourceLifecycleImpactUrl(
        workspaceId,
        'docs',
        operation,
        'docId',
        docIds,
        targetFolderId
      )
    );
  return response.impacts;
}

export async function fetchFolderAppResourceLifecycleImpacts(
  workspaceId: string,
  operation: AppResourceLifecycleOperation,
  folderIds: readonly string[],
  targetFolderId?: string | null
): Promise<AppResourceLifecycleImpact[]> {
  const response =
    await requestAppResourceJson<AppResourceLifecycleImpactResponse>(
      appResourceLifecycleImpactUrl(
        workspaceId,
        'folders',
        operation,
        'folderId',
        folderIds,
        targetFolderId
      )
    );
  return response.impacts;
}

export function AppResourceLifecycleImpactWarning({
  impacts,
}: {
  impacts: readonly AppResourceLifecycleImpact[];
}): ReactElement | null {
  const t = useI18n();
  if (impacts.length === 0) return null;

  return (
    <section data-testid="app-resource-lifecycle-impact-warning" role="note">
      <ul>
        {impacts.map(impact => (
          <li
            key={`${impact.resourceKind}:${impact.resourceId}:${impact.impact}`}
          >
            {formatAppResourceLifecycleImpact(impact, t)}
          </li>
        ))}
      </ul>
    </section>
  );
}

export function formatAppResourceLifecycleImpact(
  impact: AppResourceLifecycleImpact,
  t: I18nFuncs
): string {
  const name = `${resourceKindLabel(impact.resourceKind, t)} "${impact.title}"`;
  if (impact.impact === 'linked_resource_requires_same_area') {
    return t[
      'com.streamlain.appResourceLifecycle.linkedResourceRequiresSameArea'
    ]({
      name,
    });
  }
  if (impact.impact === 'provider_actions_disabled') {
    return t['com.streamlain.appResourceLifecycle.providerActionsDisabled']({
      name,
    });
  }
  if (impact.impact === 'public_route_disabled') {
    return t['com.streamlain.appResourceLifecycle.publicRouteDisabled']({
      name,
    });
  }
  if (impact.impact === 'will_be_read_only') {
    return t['com.streamlain.appResourceLifecycle.willBeReadOnly']({ name });
  }
  if (impact.impact === 'will_be_removed_from_apps') {
    return t['com.streamlain.appResourceLifecycle.willBeRemovedFromApps']({
      name,
    });
  }
  return t['com.streamlain.appResourceLifecycle.willBeRestoredInApps']({
    name,
  });
}

function appResourceLifecycleImpactUrl(
  workspaceId: string,
  route: 'docs' | 'folders',
  operation: AppResourceLifecycleOperation,
  idParam: 'docId' | 'folderId',
  ids: readonly string[],
  targetFolderId?: string | null
): string {
  const query = new URLSearchParams({ operation });
  for (const id of ids) {
    query.append(idParam, id);
  }
  if (targetFolderId) {
    query.set('targetFolderId', targetFolderId);
  }
  return `/api/workspace/${encodeURIComponent(
    workspaceId
  )}/app-resources/lifecycle-impact/${route}?${query.toString()}`;
}

function resourceKindLabel(
  resourceKind: AppResourceKind,
  t: I18nFuncs
): string {
  if (resourceKind === 'calendar') {
    return t['com.streamlain.appResourceLifecycle.resource.calendar']();
  }
  if (resourceKind === 'mailbox') {
    return t['com.streamlain.appResourceLifecycle.resource.mailbox']();
  }
  if (resourceKind === 'custom_app') {
    return t['com.streamlain.appResourceLifecycle.resource.customApp']();
  }
  if (resourceKind === 'kanban') {
    return t['com.streamlain.appResourceLifecycle.resource.kanban']();
  }
  return t['com.streamlain.appResourceLifecycle.resource.default']();
}
